import { useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { YStack } from '@/components/ui';
import {
  FEED_ROW_SLOT_CLASS,
  FEED_ROW_SLOT_PADDING,
  FeedDensityContext,
} from '@/features/action-cards-view/feed';
import { useRoutineRefresh } from '@/features/routines/use-routine-refresh';
import { useVisibleRoutines } from '@/features/settings/proactive-agent.query';
import { useTabs } from '@/features/tabs';
import { pinnableRoutines } from '@/features/widgets/routine-report';
import { Widget } from '@/features/widgets/widget.parts';
import type { RoutineIslandEntry } from '@/features/widgets/widgets.types';
import { cn } from '@/lib/utils';

import { HomeRoutineRow } from './components/HomeRoutineRow';
import {
  homePreferencesStore,
  type HomeSectionId,
} from './home-preferences.store';
import { HOME_FEED_DENSITY } from './need-you-cards';

const SECTION_ID: HomeSectionId = 'pinned-routines';

export const HomeRoutinesSection = observer(
  ({ entries }: { readonly entries: readonly RoutineIslandEntry[] }) => {
    const { routines } = useStores();
    const { openTab } = useTabs();
    useRoutineRefresh();
    const visibleRoutines = useVisibleRoutines(routines.userRoutines);
    const [openId, setOpenId] = useState<number>();

    useEffect(
      () => homePreferencesStore.erasePinsOfDeletedRoutines(routines),
      [routines]
    );

    const pinnable = pinnableRoutines(visibleRoutines);
    const shown = entries.flatMap((entry) => {
      const routine = routines.getByReferenceId(entry.routineId);
      return routine && pinnable.includes(routine) ? [{ entry, routine }] : [];
    });
    if (shown.length === 0) return null;

    return (
      <Widget id={SECTION_ID}>
        <Widget.Header
          title="Routines"
          count={shown.length}
          accessory={
            <Widget.HeaderButton
              onClick={() =>
                openTab({ path: '/routines' }, { reuseExisting: true })
              }
            >
              Open Routines
            </Widget.HeaderButton>
          }
        />
        <Widget.Body className="px-2">
          <FeedDensityContext.Provider value={HOME_FEED_DENSITY}>
            <YStack data-feed-density={HOME_FEED_DENSITY} className="no-record">
              {shown.map(({ entry, routine }) => (
                <div
                  key={entry.routineId}
                  className={cn(
                    FEED_ROW_SLOT_CLASS.home,
                    FEED_ROW_SLOT_PADDING[HOME_FEED_DENSITY]
                  )}
                >
                  <HomeRoutineRow
                    routineId={entry.routineId}
                    title={routine.title}
                    routine={routine}
                    open={openId === entry.routineId}
                    onToggle={() =>
                      setOpenId((current) =>
                        current === entry.routineId
                          ? undefined
                          : entry.routineId
                      )
                    }
                  />
                </div>
              ))}
            </YStack>
          </FeedDensityContext.Provider>
        </Widget.Body>
      </Widget>
    );
  }
);
